import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from './AuthContext';
import { withProductEmail } from '../utils/productEventEmail';

type Realm = 'outer' | 'workspace'; // 'outer' = dashboard, 'workspace' = memory workspace

interface RealmContextType {
  currentRealm: Realm;
  isTransitioning: boolean;
  enterWorkspace: () => void;
  exitWorkspace: () => void;
}

const RealmContext = createContext<RealmContextType | undefined>(undefined);

export function RealmProvider({ children }: { children: React.ReactNode }) {
  const [currentRealm, setCurrentRealm] = useState<Realm>('outer');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const previousRealmRef = useRef<Realm>('outer');
  const isRouteTransitionRef = useRef(false);
  const posthog = usePostHog();
  const { user } = useAuth();
  const captureProduct = useCallback(
    (event: string, properties: Record<string, unknown>) => {
      posthog?.capture(event, withProductEmail(properties, user?.email));
    },
    [posthog, user?.email],
  );

  // Sync realm state with route
  useEffect(() => {
    const pathname = location.pathname;
    let targetRealm: Realm;
    
    if (pathname.startsWith('/spaces') || pathname === '/workspace') {
      targetRealm = 'workspace';
    } else if (pathname.startsWith('/dashboard') || pathname.startsWith('/analytics')) {
      targetRealm = 'outer';
    } else {
      return; // No realm change needed for other routes
    }

    // Only update if the realm actually changed
    if (currentRealm !== targetRealm) {
      isRouteTransitionRef.current = true;
      setCurrentRealm(targetRealm);
    }
  }, [location.pathname, currentRealm]);

  // Track realm changes (only when realm actually changes)
  useEffect(() => {
    // Prevent duplicate analytics events during route transitions
    if (previousRealmRef.current !== currentRealm) {
      // Only track if this isn't part of a manual transition (which has its own tracking)
      if (!isTransitioning && !isRouteTransitionRef.current) {
        captureProduct('realm_entry', {
          realm: currentRealm,
          transitionTime: 0,
          source: 'route_change'
        });
      }
      previousRealmRef.current = currentRealm;
      isRouteTransitionRef.current = false;
    }
  }, [currentRealm, isTransitioning, captureProduct]);

  const enterWorkspace = useCallback(() => {
    if (isTransitioning || currentRealm === 'workspace') return;
    
    setIsTransitioning(true);
    const startTime = performance.now();
    
    // Track funnel step
    captureProduct('funnel_step', {
      step: 'enter_workspace_initiated',
      success: true,
      source: 'portal_button'
    });

    setTimeout(() => {
      const transitionTime = performance.now() - startTime;
      setCurrentRealm('workspace');
      setIsTransitioning(false);
      
      // Navigate to spaces route
      navigate('/spaces', { replace: true });
      
      // Track successful realm entry with timing
      captureProduct('realm_entry', {
        realm: 'workspace',
        transitionTime,
        source: 'portal_button'
      });
      captureProduct('realm_transition_completed', {
        direction: 'outer_to_workspace',
        transitionTime,
        success: true
      });
    }, 700); // Match transition duration
  }, [isTransitioning, currentRealm, navigate, captureProduct]);

  const exitWorkspace = useCallback(() => {
    if (isTransitioning || currentRealm === 'outer') return;
    
    setIsTransitioning(true);
    const startTime = performance.now();
    
    // Track exit reason and funnel
    captureProduct('funnel_step', {
      step: 'exit_workspace_initiated',
      success: true,
      source: 'portal_button'
    });

    setTimeout(() => {
      const transitionTime = performance.now() - startTime;
      captureProduct('realm_exit', {
        realm: 'workspace',
        reason: 'user_action',
        source: 'portal_button'
      });
      setCurrentRealm('outer');
      setIsTransitioning(false);
      
      // Navigate back to dashboard
      navigate('/dashboard', { replace: true });
      
      // Track successful realm exit with timing
      captureProduct('realm_entry', {
        realm: 'outer',
        transitionTime,
        source: 'portal_button'
      });
      captureProduct('realm_transition_completed', {
        direction: 'workspace_to_outer',
        transitionTime,
        success: true
      });
    }, 700);
  }, [isTransitioning, currentRealm, navigate, captureProduct]);

  const value = {
    currentRealm,
    isTransitioning,
    enterWorkspace,
    exitWorkspace,
  };

  return (
    <RealmContext.Provider value={value}>
      {children}
    </RealmContext.Provider>
  );
}

export function useRealm() {
  const context = useContext(RealmContext);
  if (context === undefined) {
    throw new Error('useRealm must be used within a RealmProvider');
  }
  return context;
} 